import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import Layout from "../../components/layout/Layout";
import Panel from "../../components/brand/Panel";
import KpiTile from "../../components/brand/KpiTile";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { getPropertyDetails } from "../../api/propertyApi";
import {
  createBudget,
  deleteBudget,
  generateCurrentInvoices,
  generateOwnerInvoicePdf,
  getBudget,
  getBudgets,
  getBudgetTemplates,
  getCurrentInvoicePreview,
  getFiscalYears,
  getPropertyFinanceSummary,
  postAllCurrentDraftInvoices,
  postOwnerInvoice,
  updateBudget,
  updateBudgetStatus,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import BudgetEditor from "./BudgetEditor";
import BudgetFormatEditor from "./BudgetFormatEditor";
import FiscalYearsPanel from "./FiscalYearsPanel";
import ApportionmentsPanel from "./ApportionmentsPanel";
import ServiceChargeMechanismPanel from "./ServiceChargeMechanismPanel";
import ServiceChargeRecalculationPanel from "./ServiceChargeRecalculationPanel";
import OwnerInvoicePreviewPanel from "./OwnerInvoicePreviewPanel";

const FINANCE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "budgets", label: "Budgets" },
  { key: "fiscal-years", label: "Fiscal Years" },
  { key: "apportionments", label: "Apportionments" },
  { key: "invoices", label: "Owner invoices" },
  { key: "bank", label: "Bank" },
  { key: "reconciliation", label: "Reconciliation" },
];

const TAB_INDEX = FINANCE_TABS.reduce((acc, t, i) => ({ ...acc, [t.key]: i }), {});

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export default function PropertyFinancePage() {
  const { propertyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();

  const requestedTabKey = searchParams.get("tab") || "overview";
  const activeTab = TAB_INDEX[requestedTabKey] ?? 0;
  const activeTabKey = FINANCE_TABS[activeTab]?.key || "overview";
  const budgetIdParam = searchParams.get("budgetId");

  const [property, setProperty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recalcRefreshKey, setRecalcRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const [budgets, setBudgets] = useState([]);
  const [budgetsPage, setBudgetsPage] = useState(1);
  const [budgetsTotalPages, setBudgetsTotalPages] = useState(1);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [deletingBudgetId, setDeletingBudgetId] = useState(null);

  const [generatingPdfId, setGeneratingPdfId] = useState(null);
  const [regeneratingAllPdfs, setRegeneratingAllPdfs] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);

  const [createBudgetOpen, setCreateBudgetOpen] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [newBudget, setNewBudget] = useState({ fiscal_year_id: "", name: "" });
  const [createBudgetSubmitting, setCreateBudgetSubmitting] = useState(false);
  const [createBudgetError, setCreateBudgetError] = useState("");
  const [budgetTemplateReady, setBudgetTemplateReady] = useState(false);

  const openCreateBudgetDialog = useCallback(async () => {
    setCreateBudgetError("");
    setError("");
    try {
      const res = await getFiscalYears(propertyId);
      const years = res.data.results || [];
      setFiscalYears(years);
      setNewBudget({
        fiscal_year_id: years.length === 1 ? String(years[0].id) : "",
        name: "",
      });
      setCreateBudgetOpen(true);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load fiscal years."));
    }
  }, [propertyId]);

  const goTab = useCallback(
    (key, extra = {}) => {
      const next = new URLSearchParams(searchParams);
      if (key === "overview") next.delete("tab");
      else next.set("tab", key);
      Object.entries(extra).forEach(([k, v]) => {
        if (v == null || v === "") next.delete(k);
        else next.set(k, String(v));
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [propertyData, summaryRes] = await Promise.all([
        getPropertyDetails(propertyId),
        getPropertyFinanceSummary(propertyId),
      ]);
      setProperty(propertyData);
      setSummary(summaryRes.data);
      setCustomPath([
        { label: "Properties", path: "/properties" },
        { label: propertyData?.name || "Property", path: `/properties/${propertyId}/details` },
        { label: "Finance", path: null },
      ]);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load property finance."));
    } finally {
      setLoading(false);
    }
  }, [propertyId, setCustomPath]);

  useEffect(() => () => clearCustomPath(), [clearCustomPath]);

  const loadBudgets = useCallback(async () => {
    const res = await getBudgets(propertyId, budgetsPage);
    setBudgets(res.data.results || []);
    setBudgetsTotalPages(res.data.total_pages || 1);
  }, [propertyId, budgetsPage]);

  const loadBudgetDetail = useCallback(async (budgetId) => {
    const budgetRes = await getBudget(propertyId, budgetId);
    setSelectedBudget(budgetRes.data);
  }, [propertyId]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  const loadBudgetTemplateStatus = useCallback(async () => {
    try {
      const res = await getBudgetTemplates(propertyId);
      setBudgetTemplateReady((res.data.results || []).length > 0);
    } catch {
      setBudgetTemplateReady(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (activeTabKey === "fiscal-years") {
      getFiscalYears(propertyId).then((r) => setFiscalYears(r.data.results || []));
    }
    if (activeTabKey === "budgets") {
      loadBudgets();
      loadBudgetTemplateStatus();
      getFiscalYears(propertyId).then((r) => setFiscalYears(r.data.results || []));
      if (budgetIdParam) loadBudgetDetail(budgetIdParam);
      else setSelectedBudget(null);
    }
    if (activeTabKey === "apportionments" && budgetIdParam) {
      loadBudgetDetail(budgetIdParam);
    }
    if (activeTabKey === "invoices") {
      loadCore();
    }
  }, [
    activeTabKey,
    budgetIdParam,
    loadBudgets,
    loadBudgetDetail,
    loadBudgetTemplateStatus,
    loadCore,
    propertyId,
  ]);

  const handleCreateBudget = async () => {
    if (!newBudget.fiscal_year_id) {
      setCreateBudgetError("Select a fiscal year.");
      return;
    }
    try {
      setCreateBudgetSubmitting(true);
      setCreateBudgetError("");
      setError("");
      const res = await createBudget(propertyId, {
        name: newBudget.name,
        fiscal_year_id: Number(newBudget.fiscal_year_id),
      });
      setCreateBudgetOpen(false);
      setNewBudget({ fiscal_year_id: "", name: "" });
      goTab("budgets", { budgetId: res.data.id });
      showToast("Budget created.");
      loadBudgets();
    } catch (e) {
      setCreateBudgetError(getApiErrorMessage(e, "Failed to create budget."));
    } finally {
      setCreateBudgetSubmitting(false);
    }
  };

  const handleBudgetSave = async (payload) => {
    try {
      const res = await updateBudget(propertyId, selectedBudget.id, payload);
      setSelectedBudget(res.data);
      showToast("Budget saved.");
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        setError("This budget was changed elsewhere. Reloading…");
        loadBudgetDetail(selectedBudget.id);
      } else {
        setError(getApiErrorMessage(e, "Failed to save budget."));
      }
    }
  };

  const handleBudgetStatus = async (action) => {
    try {
      const res = await updateBudgetStatus(propertyId, selectedBudget.id, {
        action,
        row_version: selectedBudget.row_version,
        visible_to_owners: selectedBudget.visible_to_owners,
      });
      setSelectedBudget(res.data);
      showToast(`Budget ${action.replace("_", " ")}.`);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update budget status."));
    }
  };

  const handleDeleteBudget = async (budget) => {
    if (!window.confirm(`Delete draft budget "${budget.name}"?`)) return;

    const nextPage = budgets.length === 1 && budgetsPage > 1 ? budgetsPage - 1 : budgetsPage;

    try {
      setDeletingBudgetId(budget.id);
      setError("");
      await deleteBudget(propertyId, budget.id);

      const budgetsRes = await getBudgets(propertyId, nextPage);
      const nextBudgets = budgetsRes.data.results || [];

      setBudgets(nextBudgets);
      setBudgetsTotalPages(budgetsRes.data.total_pages || 1);
      if (nextPage !== budgetsPage) {
        setBudgetsPage(nextPage);
      }

      if (String(budget.id) === budgetIdParam) {
        const nextSelectedBudget = nextBudgets[0] || null;
        setSelectedBudget(null);
        goTab("budgets", { budgetId: nextSelectedBudget?.id ?? "" });
      }

      showToast("Budget deleted.");
      loadCore();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete budget."));
    } finally {
      setDeletingBudgetId(null);
    }
  };

  const loadInvoicePreview = useCallback(async () => {
    if (!summary?.invoice_budget_ready) {
      setInvoicePreview(null);
      return;
    }
    try {
      setInvoicePreviewLoading(true);
      const res = await getCurrentInvoicePreview(propertyId);
      setInvoicePreview(res.data);
    } catch (e) {
      setInvoicePreview(null);
      setError(getApiErrorMessage(e, "Failed to load invoice preview."));
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, [propertyId, summary?.invoice_budget_ready]);

  useEffect(() => {
    if (activeTabKey === "invoices" && summary?.invoice_budget_ready) {
      loadInvoicePreview();
    }
  }, [activeTabKey, summary?.invoice_budget_ready, loadInvoicePreview]);

  const handlePreviewInvoices = async () => {
    await loadInvoicePreview();
  };

  const handleGenerateInvoices = async () => {
    try {
      setGeneratingInvoices(true);
      await generateCurrentInvoices(propertyId, {
        invoice_date: new Date().toISOString().slice(0, 10),
      });
      showToast("Draft invoices created.");
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to generate invoices."));
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const handlePostInvoice = async (invoice) => {
    try {
      await postOwnerInvoice(propertyId, invoice.id, invoice.row_version);
      showToast(`Invoice ${invoice.invoice_number} posted.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoice."));
    }
  };

  const handleGenerateInvoicePdf = async (invoice) => {
    try {
      setGeneratingPdfId(invoice.id);
      setError("");
      const res = await generateOwnerInvoicePdf(propertyId, invoice.id);
      const updated = res.data;
      showToast(`PDF generated for ${invoice.invoice_number}.`);
      if (updated.pdf_download_url) {
        window.open(updated.pdf_download_url, "_blank", "noopener,noreferrer");
      }
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to generate invoice PDF."));
    } finally {
      setGeneratingPdfId(null);
    }
  };

  const handleRegenerateAllInvoicePdfs = async () => {
    const invoices = (invoicePreview?.units || []).filter(
      (unit) => unit.invoice_id && unit.invoice_status === "draft"
    );
    if (!invoices.length) return;

    try {
      setRegeneratingAllPdfs(true);
      setError("");
      for (const invoice of invoices) {
        await generateOwnerInvoicePdf(propertyId, invoice.invoice_id);
      }
      showToast(`Regenerated ${invoices.length} invoice PDF${invoices.length === 1 ? "" : "s"}.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to regenerate invoice PDFs."));
    } finally {
      setRegeneratingAllPdfs(false);
    }
  };

  const handlePostAll = async () => {
    try {
      const res = await postAllCurrentDraftInvoices(propertyId);
      showToast(`Posted ${res.data.posted_count} invoices.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoices."));
    }
  };

  const invoiceBudgetReady = Boolean(summary?.invoice_budget_ready);

  return (
    <Layout>
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Finance
        </Typography>
        <Typography variant="h4" fontWeight={700}>
          {property?.name || "Property finance"}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, idx) => goTab(FINANCE_TABS[idx].key)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {FINANCE_TABS.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {activeTabKey === "overview" && (
        <>
          {loading ? (
            <Skeleton height={200} />
          ) : (
            <>
              {summary?.service_charge_recalculation && (
                <Alert severity="warning" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                  {summary.service_charge_recalculation.reason ||
                    "Budget or apportionment changes require a service charge recalculation."}
                </Alert>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                  mb: 3,
                }}
              >
                <KpiTile label="Budget total" value={money(summary?.budget_total)} />
                <KpiTile label="Collected YTD" value={money(summary?.collected_ytd)} />
                <KpiTile label="Spent YTD" value={money(summary?.spent_ytd)} />
                <KpiTile label="Outstanding" value={money(summary?.outstanding_ytd)} />
              </Box>
              <ServiceChargeMechanismPanel
                propertyId={propertyId}
                onMessage={showToast}
                onError={setError}
                onChanged={() => {
                  loadCore();
                  setRecalcRefreshKey((k) => k + 1);
                }}
              />
              <ServiceChargeRecalculationPanel
                propertyId={propertyId}
                refreshKey={recalcRefreshKey}
                recalculationNeeded={Boolean(summary?.service_charge_recalculation)}
                recalculationReason={summary?.service_charge_recalculation?.reason}
                onApplied={loadCore}
                onMessage={showToast}
                onError={setError}
              />
              <Panel title="Arrears" noBodyPadding={false}>
                {(summary?.arrears?.results || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No arrears over three months.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Unit</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell align="right">Outstanding</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(summary?.arrears?.results || []).map((row) => (
                          <TableRow key={row.unit_id}>
                            <TableCell>
                              <RouterLink to={`/units/${row.unit_id}/statement`}>
                                {row.unit_name}
                              </RouterLink>
                            </TableCell>
                            <TableCell>{row.owner_name || "—"}</TableCell>
                            <TableCell align="right">{money(row.outstanding)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Panel>
            </>
          )}
        </>
      )}

      {activeTabKey === "budgets" && (
        <>
          <BudgetFormatEditor
            selectedBudget={selectedBudget}
            budgetId={budgetIdParam}
            onSaved={async () => {
              if (budgetIdParam) await loadBudgetDetail(budgetIdParam);
            }}
            propertyId={propertyId}
            propertyName={property?.name}
            onMessage={(msg) => {
              showToast(msg);
              loadBudgetTemplateStatus();
            }}
            onError={setError}
          />
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "280px 1fr" }, gap: 2 }}>
          <Panel
            title="Budgets"
            noBodyPadding={false}
            right={
              <Button size="small" startIcon={<AddIcon />} onClick={openCreateBudgetDialog}>
                New
              </Button>
            }
          >
            {budgets.map((b) => (
              <Box
                key={b.id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor: String(b.id) === budgetIdParam ? "action.selected" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => goTab("budgets", { budgetId: b.id })}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {b.name}
                    </Typography>
                    <Chip size="small" label={b.status} sx={{ mt: 0.5 }} />
                  </Box>
                  {b.status === "draft" && (
                    <IconButton
                      size="small"
                      disabled={deletingBudgetId === b.id}
                      aria-label={`Delete ${b.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBudget(b);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
            {budgetsTotalPages > 1 && (
              <Pagination
                size="small"
                count={budgetsTotalPages}
                page={budgetsPage}
                onChange={(_, p) => setBudgetsPage(p)}
              />
            )}
          </Panel>
          <Box>
            {selectedBudget ? (
              <BudgetEditor
                budget={selectedBudget}
                onSave={handleBudgetSave}
                onStatus={handleBudgetStatus}
              />
            ) : (
              <Typography color="text.secondary">Select a budget to edit.</Typography>
            )}
          </Box>
        </Box>
        </>
      )}

      {activeTabKey === "fiscal-years" && (
        <FiscalYearsPanel
          propertyId={propertyId}
          financialYearStartDay={property?.financial_year_start_day}
          financialYearStartMonth={property?.financial_year_start_month}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "apportionments" && (
        <ApportionmentsPanel
          propertyId={propertyId}
          propertyUnits={property?.units || []}
          budgetIdParam={budgetIdParam}
          onBudgetChange={(id) => goTab("apportionments", { budgetId: id })}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "invoices" && (
        <Panel title="Owner invoices" noBodyPadding={false}>
          <Box sx={{ mb: 2 }}>
            {summary?.current_fiscal_year_label ? (
              <Typography variant="body2" color="text.secondary">
                Current fiscal year: {summary.current_fiscal_year_label}
                {summary.current_budget_name
                  ? ` · Budget: ${summary.current_budget_name}${
                      summary.current_budget_status
                        ? ` (${summary.current_budget_status})`
                        : ""
                    }`
                  : ""}
              </Typography>
            ) : (
              <Alert severity="warning" sx={{ mb: 0 }}>
                Set a current fiscal year before generating invoices.
              </Alert>
            )}
            {summary?.current_fiscal_year_label && !invoiceBudgetReady && (
              <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                {summary.current_budget_name
                  ? `The budget "${summary.current_budget_name}" must be approved before generating invoices.`
                  : "Create and approve a budget for the current fiscal year before generating invoices."}
              </Alert>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={handleGenerateInvoices}
              disabled={
                !invoiceBudgetReady || invoicePreview?.new_unit_count === 0 || generatingInvoices
              }
              startIcon={
                generatingInvoices ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              Generate drafts
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handlePostAll}
              disabled={!invoiceBudgetReady}
            >
              Post all drafts
            </Button>
          </Box>
          <OwnerInvoicePreviewPanel
            preview={invoicePreview}
            loading={invoicePreviewLoading}
            onRefresh={invoiceBudgetReady ? handlePreviewInvoices : null}
            onPostInvoice={handlePostInvoice}
            onGeneratePdf={handleGenerateInvoicePdf}
            generatingPdfId={generatingPdfId}
            onRegenerateAll={handleRegenerateAllInvoicePdfs}
            regeneratingAll={regeneratingAllPdfs}
          />
        </Panel>
      )}

      {["bank", "reconciliation"].includes(activeTabKey) && (
        <Panel title={FINANCE_TABS[activeTab].label} noBodyPadding={false}>
          <Typography color="text.secondary">TBC</Typography>
        </Panel>
      )}

      <Dialog
        open={createBudgetOpen}
        onClose={() => !createBudgetSubmitting && setCreateBudgetOpen(false)}
      >
        <DialogTitle>Create budget</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 360, pt: 1 }}>
          {!budgetTemplateReady && (
            <Alert severity="warning">
              Save a budget format (Estate Charge and blocks) before creating a budget.
            </Alert>
          )}
          {createBudgetError && (
            <Alert severity="error" onClose={() => setCreateBudgetError("")}>
              {createBudgetError}
            </Alert>
          )}
          {fiscalYears.length === 0 ? (
            <Alert severity="info">
              No fiscal years yet. Add one on the{" "}
              <Button size="small" onClick={() => goTab("fiscal-years")}>
                Fiscal Years
              </Button>{" "}
              tab first.
            </Alert>
          ) : (
            <FormControl>
              <InputLabel>Fiscal year</InputLabel>
              <Select
                label="Fiscal year"
                value={newBudget.fiscal_year_id}
                onChange={(e) =>
                  setNewBudget((b) => ({ ...b, fiscal_year_id: e.target.value }))
                }
              >
                {fiscalYears.map((fy) => (
                  <MenuItem key={fy.id} value={String(fy.id)}>
                    {fy.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Budget name"
            value={newBudget.name}
            onChange={(e) => setNewBudget((b) => ({ ...b, name: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBudgetOpen(false)} disabled={createBudgetSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateBudget}
            disabled={
              createBudgetSubmitting ||
              !newBudget.fiscal_year_id ||
              !budgetTemplateReady
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
